import React, { useId } from "react";
import { useRef, useEffect } from 'react';
import { IGatsbyImageData } from "gatsby-plugin-image";
import Swiper from 'swiper';
import { EffectFade, Navigation, A11y, FreeMode, Thumbs } from 'swiper/modules';
import GatsbyImageTag from "components/partials/parts/gatsby-image-tag";
import { ReactComponent as CaretLeftIcon } from "img/arrows/caret-left.svg";
import { ReactComponent as CaretRightIcon } from "img/arrows/caret-right.svg";

export type Card = {
    gatsbyImageData?: IGatsbyImageData;  // suggested size: 940x610
    caption?: string;
    alt?: string;
};

export type ImageGalleryProps = {
    cards: Card[];
};

const ImageGallery: React.FC<ImageGalleryProps> = ({ cards }) => {

    const swiperRef = useRef(null);

    const swiperThumbsRef = useRef(null);

    // ensures unique id for each carousel
    const id = useId().replaceAll(':', '');

    useEffect(() => {

        if (swiperThumbsRef.current !== null) {

            const swiperInstance = new Swiper(swiperThumbsRef.current, {
                modules: [FreeMode, A11y],
                spaceBetween: 8,
                slidesPerView: 5,
                loop: false,
                freeMode: true,
                breakpoints: {
                    531: {
                        spaceBetween: 16,
                    }
                }
            });

            if (process.env.GATSBY_SERVER_ENV === 'dev') {
                // Attach the swiper instance to the window object
                // for debugging purposes
                // you will need to review the rendered html to find the correct id for 
                // js-gallery-thumbs-${id} and replace XXX with the id.
                // console commands:
                // window.swiperImageGalleryThumbs.slideNext();
                // window.swiperImageGalleryThumbs.slidePrev();
                (window as any)[`swiperImageGalleryThumbs${id}`] = swiperInstance;
            }

        }

        if (swiperRef.current !== null) {
            const swiperInstance = new Swiper(swiperRef.current, {
                modules: [EffectFade, Navigation, A11y, Thumbs],
                loop: false,
                fadeEffect: { crossFade: true },
                effect: 'fade',
                navigation: {
                    nextEl: `.js-gallery-slides-${id} .swiper-nav-next`,
                    prevEl: `.js-gallery-slides-${id} .swiper-nav-prev`,
                },
                thumbs: {
                    swiper: swiperThumbsRef.current,
                },
            });

            if (process.env.GATSBY_SERVER_ENV === 'dev') {
                // Attach the swiper instance to the window object
                // for debugging purposes.
                // you will need to review the rendered html to find the correct id for 
                // js-gallery-slides${id} and replace XXX with the id.
                // console commands:
                // window.swiperImageGallery.slideNext();
                // window.swiperImageGallery.slidePrev();
                (window as any)[`swiperImageGallery${id}`] = swiperInstance;
            }

        }
    }, []);

    return (
        <div className="pattern pattern--gallery">
            <div className="container container--md">
                <div className="gallery">
                    <div className={`swiper gallery__slideshow js-gallery-slides js-gallery-slides-${id}`} ref={swiperRef}>
                        <div className="swiper-wrapper">
                            {cards.map((card, i) => (
                                <div className="swiper-slide" key={i}>
                                    <GatsbyImageTag image={card.gatsbyImageData} alt={card.alt} />
                                    {card.caption && <div className="gallery-image-caption">
                                        {card.caption}
                                    </div>}
                                </div>
                            ))}
                        </div>

                        <div className="swiper-nav-prev btn-circle-arrow">
                            <CaretLeftIcon />
                        </div>
                        <div className="swiper-nav-next btn-circle-arrow">
                            <CaretRightIcon />
                        </div>
                    </div>

                    <div className={`swiper gallery__thumbs js-gallery-thumbs js-gallery-thumbs-${id}`} ref={swiperThumbsRef}>
                        <div className="swiper-wrapper">
                            {cards.map((card, i) => (
                                <div className="swiper-slide" key={i}>
                                    <GatsbyImageTag image={card.gatsbyImageData} alt={card.alt} />
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ImageGallery;